import React, { ReactNode, useEffect, useState } from 'react';

import { ModelContext, ModelFile, ModelLayer } from './ModelContext';

export const ModelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [uploadedModels, setUploadedModels] = useState<ModelFile[] | null>(null);
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const [modelLayers, setModelLayers] = useState<ModelLayer[]>([]);
  const [modelLoadingError, setModelLoadingError] = useState<string | null>(null);
  const [metersPerUnit, setMetersPerUnit] = useState<number>(1);
  const [scaleCalibrated, setScaleCalibrated] = useState<boolean>(false);

  // Reset calibration whenever the uploaded model set changes — user must re-calibrate per upload.
  useEffect(() => {
    setMetersPerUnit(1);
    setScaleCalibrated(false);
  }, [uploadedModels]);

  // Generate default layers for demo purposes
  useEffect(() => {
    // Only run this effect if we have uploaded models but no layers yet
    if (uploadedModels && uploadedModels.length > 0 && modelLayers.length === 0) {
      // For each model, create some sample layers
      const newLayers: ModelLayer[] = [];

      // TODO: some error if obj doesn't have layers

      setModelLayers(newLayers);
    }
  }, [uploadedModels, modelLayers.length]);

  const addModelLayers = (modelName: string, threeJsLayerNames: string[], originalLayerNames: string[]) => {
    // Use functional update so the dedup check always sees the latest state,
    // even when called multiple times in the same render cycle.
    setModelLayers((prev) => {
      const existingThreeJsLayerNames = prev
        .filter((layer) => layer.modelName === modelName)
        .map((layer) => layer.threeJsName);

      const newIndexes = threeJsLayerNames
        .map((name, i) => ({ name, i }))
        .filter(({ name }) => !existingThreeJsLayerNames.includes(name));

      if (newIndexes.length === 0) return prev; // no change → skip re-render

      const newLayers = newIndexes.map(({ name, i }) => ({
        uuid: crypto.randomUUID(),
        threeJsName: name,
        originalName: originalLayerNames[i],
        displayName: originalLayerNames[i],
        modelName: modelName,
        visible: true,
      }));

      return [...prev, ...newLayers];
    });
  };

  const updateLayerDisplayName = (layerUuid: string, displayName: string) => {
    setModelLayers((prev) => prev.map((layer) => (layer.uuid === layerUuid ? { ...layer, displayName } : layer)));
  };

  const updateLayerColor = (layerUuid: string, color: string) => {
    setModelLayers((prev) => prev.map((layer) => (layer.uuid === layerUuid ? { ...layer, color } : layer)));
  };

  const toggleLayerVisibility = (layerName: string) => {
    setModelLayers((prev) =>
      prev.map((layer) => (layer.threeJsName === layerName ? { ...layer, visible: !layer.visible } : layer)),
    );
  };

  const toggleAllLayersVisibility = (visible: boolean) => {
    setModelLayers((prev) => prev.map((layer) => ({ ...layer, visible })));
  };

  const onModelLoadingError = (modelName: string, error: string) => {
    const errorMessage = `Failed to load model "${modelName}": ${error}`;
    setModelLoadingError(errorMessage);
  };

  return (
    <ModelContext.Provider
      value={{
        uploadedModels,
        setUploadedModels,
        selectedLayers,
        setSelectedLayers,
        modelLayers,
        setModelLayers,
        addModelLayers,
        updateLayerDisplayName,
        updateLayerColor,
        toggleLayerVisibility,
        toggleAllLayersVisibility,
        modelLoadingError,
        setModelLoadingError,
        onModelLoadingError,
        metersPerUnit,
        setMetersPerUnit,
        scaleCalibrated,
        setScaleCalibrated,
      }}
    >
      {children}
    </ModelContext.Provider>
  );
};
